/**
 * Community creation form
 *
 */

/**
 * React Redux
 */
import React, {useEffect, useState} from 'react';
import { Field, reduxForm } from 'redux-form';
import { isEmpty } from 'lodash';

/**
 * Fabric UI
 */
import {
    PrimaryButton,
    DefaultButton
} from '@fluentui/react/lib/Button';
import { Icon } from '@fluentui/react/lib/Icon';
import { Label } from '@fluentui/react/lib/Label';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { ResponsiveMode } from '@fluentui/react';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { Dialog, DialogFooter } from '@fluentui/react/lib/Dialog';

/*
 * Fabric UI customised
 */
import TextField from '../../FormFields/TextField';
import Dropdown from '../../FormFields/Dropdown';
import TagPicker from '../../FormFields/TagPicker';
import RichTextEditor from '../../FormFields/RichTextEditor';
import {
    TooltipLabel,
    renderLabel
} from '../../TooltipLabel';

/**
 * GovTeams constants
 */
import getCreateFormText from './CommunityFormComponent.constants';

/**
 * GovTEAMS components
 */
import Similarity from '../../SimilarCommunityCardComponent/Similarity';
import TagPickerComponent from '../../FormFields/TagPicker';

import { CommunityType } from '../types';

const validate = (values) => {
    const errors: any = {};
    const regex = /^[a-zA-Z0-9 ()\-:&.,']+$/;

    if (!values.displayName) {
        errors.displayName = 'Please enter a valid community name';
    }

    if (!values.typeId) {
        errors.typeId = 'Please select a community type from the list';
    }

    if (!regex.test(values.displayName)) {
        errors.displayName =
            "Community names can only contain letters, numbers, spaces and the following special characters ( ) - : & . , '";
    }

    if (!regex.test(values.description)) {
        errors.description =
            "Community descriptions can only contain letters, numbers, spaces and the following special characters ( ) - : & . , '";
    }
    return errors;
};


const openCommunityExisting = [
    {
        id: '1',
        description: 'Log a ticket for us to assist you to convert this Community to Open; OR',
        link: 'https://www.govteams.gov.au/contact-us',
        linkDescription: 'Log a ticket here'
    },
    {
        id: '2',
        description: 'Create a new Open community if your content is appropriate.',
        link: '',
        linkDescription: ''
    }
];

const openCommunityNew = [
    {
        id: '1',
        description: 'Are accessible to all members, who may join your community at any time without your approval.',
        link: '',
        linkDescription: ''
    },
    {
        id: '2',
        description: 'Any GovTEAMS member can add files and content to your community without your knowledge or approval.',
        link: '',
        linkDescription: ''

    },
    {
        id: '3',
        description: 'All content is open and accessible to all GovTEAMS members and will appear in searches.',
        link: '',
        linkDescription: ''
    },
    {
        id: '4',
        description: 'Open communities need to be actively monitored and maintained by the community owners to ensure that nothing inappropriate or sensitive is posted.',
        link: '',
        linkDescription: ''
    }
];

const OpenCommunityExisting = () => (
    <ul>
        {openCommunityExisting.map(item => (
            <li key={item.id} className='li-Left'>
                <div>{item.link && (<a href={item.link} target="_blank" rel="noreferrer">{item.description}</a>)}</div>
                <div>{!item.link && (item.description)}</div>
                {/*<div>{!item.link && (<a href={item.link} target="_blank" rel="noreferrer">{item.description}</a>)}</div>*/}
            </li>
        ))}
        <p></p>
        <a href="https://www.govteams.gov.au/open-communities" target="_blank" rel="noreferrer">Learn more...</a>
    </ul>

);

const OpenCommunityNew = () => (
    <ul>
        {openCommunityNew.map(item => (
            <li key={item.id}>
                <div>{item.description}</div>
            </li>
        ))}
    </ul>
);

const CommunityFormComponent = props => {
    //const [iconPreferenceId, setIconPreferenceId] = useState(1);
    const [formValues, setFormValues] = useState({displayName: '', tags: []});
    const [enableTeams, setEnableTeams] = useState(false);
    const [enableIncludeExtendedDescToWelcomeEmail, setEnableIncludeExtendedDescToWelcomeEmail] = useState(false);
    const [enableSharePoint, setEnableSharePoint] = useState(false);
    const [hideOpenCommunityDialog, setHideOpenCommunityDialog] = useState(true);
    const [richText, setRichText] = useState('');
    const [openCommunityAccepted, setOpenCommunityAccepted] = useState(false);
    const [openCommunityHeading, setOpenCommunityHeading] = useState('');
    const [openCommunityMessage, setOpenCommunityMessage] = useState<React.ReactElement>(null);
    const [openCommunitySubmitButtonText, setOpenCommunitySubmitButtonText] = useState('Submit');
    const [communityTypeOptions, setCommunityTypeOptions] = useState([
        {
            key: 2,
            text: 'Private (recommended) - easily discoverable, however members must request to join or be invited to join.'
        },
        {
            key: 3,
            text: 'Hidden - not discoverable by anyone, members must be invited to join hidden communities.'
        },
        {
            key: 1,
            text: 'Open - easily discoverable, content is public and all members can join and view at all times.'
        },
    ]);

    const {
        isEditForm = false,
        isTeamsApp = false,
        constants,
        brandName,
        change,
        //isFormDirty,
        communityId,
        createError,
        findSimilarCommunities,
        similarCommunities,
        setFormIsDirty,
        profile,
        initialValues,
        requestFetchCommunity,
        requestSaveCommunity,
        endEditCommunity,
        isProtected,

        onSimilarCommunityClick,
        tooltipSponsoredBadge,
        tagPickerResolveUrl,
        authToken,

        initialize,
        handleSubmit,
        pristine,
        submitting,

        showTnCGTO,
        showTnCGTP,
        isTnCAccepted,
        handleTnCChange,

        communityActionCreators,
        membersActionCreators,
        inviteMembersActionCreators,
        requestsActionCreators,
    } = props;

    const {
        tooltips,
        defaults,
    } = getCreateFormText(brandName);

    const _callbackOnChange = (fieldName: string, value: any) => {
        const _values = {...formValues};
        _values[fieldName] = value;
        setFormValues({..._values});
        change(fieldName, value);
    };

    const _setIsFormDirty = (/*form*/) => {
        //Display Pop up dialog if open community is selected
        //if (form === CommunityType.Open)
        //this.showOpenCommunityDialog();
        setFormIsDirty?.();
    };

    const _findSimilarCommunities = (_event: React.MouseEvent<HTMLElement>, newValue: any) => {
        if ( newValue !== '' && (!isEditForm || !communityId) )
            findSimilarCommunities?.();
    };

    const onLoad = async () => {
        const communityId = initialValues
            ? initialValues.communityAzureId
            : null;

        if (communityId) {
            setOpenCommunityMessage(OpenCommunityExisting());
            setOpenCommunityHeading(defaults.POPUP_EXISTING_OPEN_COMMUNITY_HEADING);
            await requestFetchCommunity(communityId, true);
        }
        else {
            setOpenCommunityMessage(OpenCommunityNew());
            setOpenCommunityHeading(defaults.POPUP_NEW_OPEN_COMMUNITY_HEADING);
            setOpenCommunitySubmitButtonText("Create");
        }

        if (initialValues) {
            setEnableSharePoint(initialValues.iconPreferenceId === 1 || initialValues.iconPreferenceId === 3);
            setEnableTeams(initialValues.iconPreferenceId === 1 || initialValues.iconPreferenceId === 2);
            setRichText(initialValues?.extendedDescription);
            setEnableIncludeExtendedDescToWelcomeEmail(initialValues.includeExtendedDescToWelcomeEmail);

            const {
                communityType,
                description,
                extendedDescription,
                includeExtendedDescToWelcomeEmail,
                displayName,
                iconPreferenceId,
                tags
            } = initialValues;

            initialize({
                communityType,
                description,
                extendedDescription,
                includeExtendedDescToWelcomeEmail,
                displayName,
                iconPreferenceId,
                tags,
                typeId: getTypeId(initialValues.communityType)
            });
        }

        if (profile?.memberType === "Partner") {
            setCommunityTypeOptions(communityTypeOptions.filter(op => op.key !== 1));
        }
    };

    useEffect( () => {
        console.log('GovTEAMS library community form components v1.0.240506')
        onLoad().then(null);
    }, []);

    useEffect(() => {
        if ( !isEmpty(initialValues) ) {
            const {
                communityType,
                description,
                extendedDescription,
                displayName,
                iconPreferenceId,
                tags
            } = initialValues;

            initialize({
                communityType,
                description,
                extendedDescription,
                displayName,
                iconPreferenceId,
                tags,
                typeId: getTypeId(initialValues.communityType)
            });

            setEnableSharePoint(initialValues.iconPreferenceId === 1 || initialValues.iconPreferenceId === 3);
            setEnableTeams(initialValues.iconPreferenceId === 1 || initialValues.iconPreferenceId === 2);
            setRichText(initialValues?.extendedDescription);
            setEnableIncludeExtendedDescToWelcomeEmail(initialValues.includeExtendedDescToWelcomeEmail);
        }
    }, [initialValues]);

    useEffect(() => {
        _callbackOnChange('iconPreferenceId', getIconPreferenceId());
    }, [enableSharePoint, enableTeams]);

    useEffect(() => {
        _callbackOnChange('includeExtendedDescToWelcomeEmail', !!enableIncludeExtendedDescToWelcomeEmail);
    }, [enableIncludeExtendedDescToWelcomeEmail]);


    useEffect(() => {
        _callbackOnChange('extendedDescription', richText);
    }, [richText]);

    const onClose = (event) => {
        endEditCommunity(communityId);
        event.preventDefault();
        event.stopPropagation();
    };

    const showOpenCommunityDialog = () => {
        setHideOpenCommunityDialog(false);
    };

    const closeOpenCommunityDialog = () => {
        setHideOpenCommunityDialog(true);
        setOpenCommunityAccepted(false);
    };

    const onCheckboxChangeOpenCommunity = (ev, isChecked) => {
        setOpenCommunityAccepted(isChecked);
    };

    const onSubmit = (form) => {
        let openTypeId: number = CommunityType.Open;
        let formType: string = getTypeId(form.typeId)


        if (initialValues) {

            //If the existing community type change, and it changed to Open, display dialog box, otherwise save the changes
            if (form.typeId === openTypeId && initialValues.communityType !== CommunityType.Open && initialValues.communityType !== formType) {
                if (!createError)
                    showOpenCommunityDialog();
                else
                    return;
            }
            else {
                requestSaveCommunity(communityId);
            }
        }
            //If the existing community type is unchanged, regardless of type, allow user to save the change.
        //Otherwis for community type change from Private/Hidden to Open, display dialog box
        else {
            if (form.typeId === openTypeId) {
                if (!createError)
                    showOpenCommunityDialog();
                else
                    return;
            }
            else {
                requestSaveCommunity(communityId);
            }
        }

    };

    const onSubmitOpenCommunity = () => {
        //TO DO: Log user's I understand create the Open community.
        if (createError) {
            setHideOpenCommunityDialog(true);
            return;
        }
        if (initialValues)
            closeOpenCommunityDialog();
        else {
            requestSaveCommunity(communityId);
            closeOpenCommunityDialog();
        }
    };

    // const onKeyPress = (event) => {
    //     if (event.key === 'Enter') {
    //         event.preventDefault();
    //     }
    // };

    const getTypeId = (communityType) => {
        return CommunityType[communityType];
    }

    const getIconPreferenceId = () => {
        let id = 1;

        if (enableSharePoint && enableTeams) {
            id = 1;
        }
        if (!enableSharePoint && enableTeams) {
            id = 2;
        }
        if (enableSharePoint && !enableTeams) {
            id = 3;
        }
        return id;
    }

    return (
        <form onSubmit={handleSubmit} /*onKeyPress={this.onKeyPress}*/>
            {/*{!communityId && (
                <Prompt
                    when={isFormDirty}
                    message={(location) =>
                        `Are you sure you want to navigate away from this page?  All changes will be lost if you continue.`
                    }
                />
            )}*/}
            <div className='formFields'>
                <div className='formField'>
                    {!communityId && createError != null && (
                        <p style={{ color: 'red' }}>{createError}</p>
                    )}

                    <Field
                        id='displayName'
                        ariaLabel={tooltips.COMMUNITY_COMMUNITYNAME}
                        name='displayName'
                        component={TextField}
                        label='Community Name'
                        placeholder='Enter a Community name'
                        maxLength={256}
                        required={true}
                        onRenderLabel={renderLabel}
                        onBlur={_findSimilarCommunities}
                        onChange={_setIsFormDirty}
                    />
                </div>
                <div className='formField'>
                    <Field
                        name='tags'
                        elementName='tagsName'
                        id='tags'
                        ariaLabel={tooltips.COMMUNITY_TAGS}
                        label='Tags'
                        onResolveUrl={tagPickerResolveUrl}
                        authToken={authToken}
                        component={TagPickerComponent}
                    />
                </div>
                <div className='formField'>
                    <Field
                        ariaLabel={tooltips.COMMUNITY_DESCRIPTION}
                        name='description'
                        component={TextField}
                        label='Description'
                        placeholder='Enter a description'
                        multiline={true}
                        rows='4'
                        maxLength={1024}
                        onRenderLabel={renderLabel}
                        onChange={_setIsFormDirty}
                    />
                </div>

                <div className='formField'>
                    <RichTextEditor
                        ariaLabel={tooltips.COMMUNITY_EXTDESCRIPTION}
                        label='How to use this community'
                        defaultText={
                            communityId
                                ? ''
                                : defaults.COMMUNITY_EXTENDED_DESCRIPTION
                        }
                        value={richText}
                        onChange={(value) => {
                            _callbackOnChange('extendedDescription', value);
                        }}
                    />
                </div>
                <Field
                    name='includeExtendedDescToWelcomeEmail'
                    component={Dropdown}
                    onChange={_setIsFormDirty}
                    hidden={true}
                    options={[
                        {
                            value: 'true'
                        },
                        {
                            value: 'false'
                        }
                    ]}
                />
                <div className='formField-toggles'>
                    <div className='formField-toggleGroup'>
                        <div className='formField-toggleGroup--label'>
                            <Label>Include 'How to use this community' information above in the welcome email sent to new community members</Label>
                        </div>
                        <Toggle
                            checked={enableIncludeExtendedDescToWelcomeEmail}
                            onChange={() => {
                                setEnableIncludeExtendedDescToWelcomeEmail(prev => !prev);
                            }}
                            onText='Yes'
                            offText='No'
                        />
                    </div>
                </div>
                <div className='formField'>
                    <Dialog
                        key='dialog'
                        hidden={hideOpenCommunityDialog}
                        onDismiss={closeOpenCommunityDialog}
                        dialogContentProps={{
                            title: openCommunityHeading,
                        }}
                        modalProps={{
                            isBlocking: true,
                            isDarkOverlay: true,
                            className: 'govTeams-dialog'
                        }}
                    >

                        <div>{openCommunityMessage}</div>
                        <p></p>

                        <Checkbox
                            label='I understand.'
                            onChange={onCheckboxChangeOpenCommunity}
                        />
                        <DialogFooter>
                            <DefaultButton onClick={closeOpenCommunityDialog} text='Cancel' />
                            <PrimaryButton onClick={handleSubmit(onSubmitOpenCommunity)} disabled={!openCommunityAccepted} text={openCommunitySubmitButtonText} type='submit'/>
                        </DialogFooter>
                    </Dialog>
                </div>
                <div className='formField'>
                    <TooltipLabel
                      ariaLabel={tooltips.COMMUNITY_COMMUNITYTYPE}
                      required={true}
                      label='Community type'
                    />
                    {isProtected && (
                      <Field
                        name='typeId'
                        responsiveMode={ResponsiveMode.large}
                        component={Dropdown}
                        onChange={_setIsFormDirty}
                        placeholder='Select a community type'
                        required={true}
                        options={[
                            {
                                key: 2,
                                text:
                                  'Private (recommended) - easily discoverable, however members must request to join or be invited to join.'
                            },
                            {
                                key: 3,
                                text:
                                  'Hidden - not discoverable by anyone, members must be invited to join hidden communities.'
                            }
                        ]}
                      />
                    )}
                    {!isProtected && (
                      <Field
                        name='typeId'
                        responsiveMode={ResponsiveMode.large}
                        component={Dropdown}
                        onChange={_setIsFormDirty}
                        placeholder='Select a community type'
                        required={true}
                        options={communityTypeOptions}
                      />
                    )}
                </div>
                {initialValues && communityId && (
                  <React.Fragment>
                      <div className='spacing-bottom'/>
                      <h2>Community tools</h2>
                      <p>
                          You can choose which tools you want to promote on your community
                          card and information page (you must have at least one). Members
                          can still access the tools, however they won’t be displayed on
                          the dashboard.
                      </p>
                        <p>You can change these settings at anytime.</p>
                      <Field
                        name='iconPreferenceId'
                        component={Dropdown}
                        onChange={_setIsFormDirty}
                        hidden={true}
                        options={[
                            {
                                key: 1,
                                text: 'SharePoint and Teams'
                            },
                            {
                                key: 2,
                                text: 'Teams'
                            },
                            {
                                key: 3,
                                text: 'SharePoint'
                            }
                        ]}
                      />
                      <div className='formField-toggles'>
                          <div className='formField-toggleGroup'>
                              <div className='formField-toggleGroup--label'>
                                  <Icon iconName='TeamsLogo'/>
                                  <Label>Enable Microsoft Teams?</Label>
                              </div>
                                <Toggle
                                    checked={enableTeams}
                                    onChange={() => {
                                        setEnableTeams(prev => !prev);
                                    }}
                                    onText='Yes'
                                    offText='No'
                                />
                            </div>
                            <div className='formField-toggleGroup'>
                                <div className='formField-toggleGroup--label'>
                                    <Icon iconName='SharepointLogo' />
                                    <Label>Enable SharePoint Online?</Label>
                                </div>
                                <Toggle
                                    checked={enableSharePoint}
                                    onChange={() => {
                                        setEnableSharePoint(prev => !prev);
                                    }}
                                    onText='Yes'
                                    offText='No'
                                />
                            </div>
                            {!enableTeams && !enableSharePoint && (
                                <span>
                                    <div role='alert'>
                                        <p
                                            className='ms-TextField-errorMessage'
                                            style={{ color: 'rgb(168, 0, 0)' }}
                                        >
                                            You cannot disable both icons. Please enable one or both.
                                        </p>
                                    </div>
                                </span>
                            )}
                        </div>
                    </React.Fragment>
                )}
            </div>

            {!communityId &&
                similarCommunities &&
                similarCommunities.length > 0 && (
                    <>
                        <Similarity
                            constants={constants}
                            profile={profile}
                            brandName={brandName}
                            tooltipSponsoredBadge={tooltipSponsoredBadge}
                            similarCommunities={similarCommunities}
                            onSimilarCommunityClick={onSimilarCommunityClick}

                            communityActionCreators={communityActionCreators}
                            membersActionCreators={membersActionCreators}
                            inviteMembersActionCreators={inviteMembersActionCreators}
                            requestsActionCreators={requestsActionCreators}
                        />
                    </>
            )}
            {isTeamsApp && (
              <div className='form-TnC'>
                  <hr style={{marginTop: '25px'}} />
                  {!isProtected &&
                    <>
                        {showTnCGTO}
                        <Checkbox
                          label={`I accept the ${brandName} terms and conditions`}
                          onChange={handleTnCChange}
                          checked={isTnCAccepted}
                        />
                    </>
                  }
                  {isProtected &&
                    <>
                        {showTnCGTP}
                        <Checkbox
                          label={`I accept the ${brandName} terms and conditions`}
                          onChange={handleTnCChange}
                          checked={isTnCAccepted}
                        />
                    </>
                  }
              </div>
            )}
            <div className='formFooter'>
                <div className='formFooter-left'>
                    {!isEditForm && !communityId && createError != null && (
                        <p style={{ color: 'red' }}>{createError}</p>
                    )}

                    {!isTeamsApp && <DefaultButton text='Cancel' onClick={onClose} /> }

                    {!communityId && (
                        <PrimaryButton
                            text={isTeamsApp ? 'Create':'Next'}
                            type='submit'
                            disabled={pristine || submitting || (isTeamsApp && !isTnCAccepted)}
                            onClick={handleSubmit(onSubmit)}
                        />
                    )}
                    {communityId && (
                        <PrimaryButton
                            text='Submit'
                            type='submit'
                            disabled={
                                pristine ||
                                submitting ||
                                (!enableTeams && !enableSharePoint && !enableIncludeExtendedDescToWelcomeEmail)
                            }
                            onClick={handleSubmit(onSubmit)}
                        />
                    )}
                </div>
            </div>
        </form>
    );
}


export default reduxForm({
    form: 'CommunityForm',
    validate
})(CommunityFormComponent);
